/* ErrorBoundary — reusable React error boundary with bilingual fallback UI.
 *
 * Catches unhandled exceptions in child components (render, lifecycle methods,
 * constructors) and displays a friendly fallback instead of a blank screen.
 * Does NOT catch async errors (Promise rejections in event handlers / effects)
 * — those should be try/catch'd at source.
 *
 * Must be a class component — React error boundaries cannot be functional.
 *
 * Usage:
 *   <ErrorBoundary label="Digital Twin">
 *     <SomePanel />
 *   </ErrorBoundary>
 *
 * Or with custom fallback:
 *   <ErrorBoundary fallback={<MyFallback />}>...</ErrorBoundary>
 */
import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportError } from "@/lib/errorReporter";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // iter734 (Feb 2026) — expose the last error via a global so users can
    // share the exact stack trace without dev tools. In production we still
    // silently absorb (per original behaviour); only console output is gated.
    try {
      if (typeof window !== "undefined") {
        window.__nutramilo_last_error = {
          label: this.props.label || "(no label)",
          message: error?.message || String(error),
          stack: error?.stack || null,
          componentStack: errorInfo?.componentStack || null,
          at: new Date().toISOString(),
          url: window.location?.href,
        };
      }
    } catch { /* noop */ }
    // iter1001 — изпрати към вградения инбокс за грешки
    try {
      reportError(error?.message || String(error), {
        stack: `${error?.stack || ""}\n--- component stack ---${errorInfo?.componentStack || ""}`,
        component: this.props.label || "ErrorBoundary",
      });
    } catch { /* noop */ }
    if (process.env.NODE_ENV === "development") {
      console.error(
        `[ErrorBoundary${this.props.label ? ` · ${this.props.label}` : ""}]`,
        error, errorInfo,
      );
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") {
      this.props.onReset();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    const bg = this.props.bg !== false;  // default to BG-first
    const label = this.props.label || (bg ? "този модул" : "this module");

    return (
      <div
        className="relative overflow-hidden rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-800 p-5 sm:p-6"
        role="alert"
        data-testid="error-boundary-fallback"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-amber-900 dark:text-amber-100 mb-1">
              {bg ? `Модулът «${label}» временно не се зареди` : `Module "${label}" didn't load`}
            </h3>
            <p className="text-[12px] text-amber-800 dark:text-amber-200 leading-snug mb-3">
              {bg
                ? `Натиснете «Опитай отново» — обикновено помага. Останалата част от страницата работи нормално. Ако проблемът продължи, презаредете.`
                : `Press "Try again" — that usually helps. The rest of the page keeps working. If it persists, reload.`}
            </p>
            {process.env.NODE_ENV === "development" && this.state.error?.message && (
              <pre className="text-[10px] font-mono bg-amber-100/50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 rounded p-2 mb-3 overflow-x-auto whitespace-pre-wrap break-all"
                   data-testid="error-boundary-dev-trace">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors"
                data-testid="error-boundary-retry-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {bg ? "Опитай отново" : "Try again"}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-white dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                data-testid="error-boundary-reload-btn"
              >
                {bg ? "Презареди страницата" : "Reload page"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
